import React from 'react';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';
import CollectVariableTreeNode from '../../../../types/BotBuilder/CollectVariableTreeNode';
import InlineEnumeratedSlotValue from '../../../../types/BotBuilder/InlineEnumeratedSlotValue';
import Slot from '../../../../types/BotBuilder/Slot';
import SlotButton from './SlotButton';
import DefaultButton from './DefaultButton';

interface SlotConditionalsProps {
  collectVariableNode: CollectVariableTreeNode | null;
  newSlotType: SlotValueType | null;
  variableMode: 'existing' | 'new';
  enumeratedValues: InlineEnumeratedSlotValue[];
  selectedSlot: Slot | null;
  nodeId: string;
  isConnectable: boolean;
  highlightedHandle?: string | null;
  onSlotValueClick?: (nodeId: string, handleId: string) => void;
}

function SlotConditionals({
  collectVariableNode,
  newSlotType,
  variableMode,
  enumeratedValues,
  selectedSlot,
  nodeId,
  isConnectable,
  highlightedHandle,
  onSlotValueClick
}: Readonly<SlotConditionalsProps>): React.ReactElement | null {
  const currentSlotType =
    variableMode === 'existing'
      ? collectVariableNode?.slotValueType
      : newSlotType;

  const slotProvidedHandleId = `${nodeId}-slot-provided-handle`;
  const fallbackHandleId = `${nodeId}-fallback-handle`;

  const handleClick = (handleId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onSlotValueClick?.(nodeId, handleId);
  };

  if (currentSlotType === SlotValueType.FREE_TEXT) {
    return (
      <div className='w-full'>
        <SlotButton
          text='Visitor enters any text'
          handleId={slotProvidedHandleId}
          isConnectable={isConnectable}
          isHighlighted={highlightedHandle === slotProvidedHandleId}
          onClick={handleClick(slotProvidedHandleId)}
        />
      </div>
    );
  }

  if (currentSlotType === SlotValueType.DATE) {
    return (
      <div className='w-full'>
        <SlotButton
          text='Visitor enters date'
          handleId={slotProvidedHandleId}
          isConnectable={isConnectable}
          isHighlighted={highlightedHandle === slotProvidedHandleId}
          onClick={handleClick(slotProvidedHandleId)}
        />
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  if (currentSlotType === SlotValueType.NUMBER) {
    return (
      <div className='w-full'>
        <SlotButton
          text='Visitor enters number'
          handleId={slotProvidedHandleId}
          isConnectable={isConnectable}
          isHighlighted={highlightedHandle === slotProvidedHandleId}
          onClick={handleClick(slotProvidedHandleId)}
        />
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  if (currentSlotType === SlotValueType.EMAIL) {
    return (
      <div className='w-full'>
        <SlotButton
          text='Visitor enters email'
          handleId={slotProvidedHandleId}
          isConnectable={isConnectable}
          isHighlighted={highlightedHandle === slotProvidedHandleId}
          onClick={handleClick(slotProvidedHandleId)}
        />
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  if (currentSlotType === SlotValueType.DOCUMENT_UPLOAD) {
    return (
      <div className='w-full'>
        <SlotButton
          text='Visitor uploads a document'
          handleId={slotProvidedHandleId}
          isConnectable={isConnectable}
          isHighlighted={highlightedHandle === slotProvidedHandleId}
          onClick={handleClick(slotProvidedHandleId)}
        />
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  if (
    currentSlotType === SlotValueType.ENUMERATED_VALUE &&
    variableMode === 'new'
  ) {
    const validValues = enumeratedValues.filter((v) => v.value);
    return (
      <div className='w-full'>
        {validValues.map(
          (enumValue: InlineEnumeratedSlotValue, idx: number) => {
            const handleId = `${nodeId}-slot-conditional-handle-${idx}`;
            return (
              <SlotButton
                key={enumValue.value ?? idx}
                text={enumValue.value}
                handleId={handleId}
                isConnectable={isConnectable}
                isHighlighted={highlightedHandle === handleId}
                onClick={handleClick(handleId)}
              />
            );
          }
        )}
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable={false}
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  const enumeratedValuesFromSlot =
    variableMode === 'existing' &&
    selectedSlot?.valueType === SlotValueType.ENUMERATED_VALUE
      ? selectedSlot.enumeratedSlotValues
      : null;

  if (enumeratedValuesFromSlot && enumeratedValuesFromSlot.length > 0) {
    return (
      <div className='w-full'>
        {enumeratedValuesFromSlot.map((enumValue, idx: number) => {
          const handleId = `${nodeId}-slot-conditional-handle-${idx}`;
          return (
            <SlotButton
              key={enumValue.resolvedValue ?? idx}
              text={enumValue.resolvedValue}
              handleId={handleId}
              isConnectable={isConnectable}
              isHighlighted={highlightedHandle === handleId}
              onClick={handleClick(handleId)}
            />
          );
        })}
        <DefaultButton
          handleId={fallbackHandleId}
          isConnectable={isConnectable}
          isClickable={false}
          isHighlighted={highlightedHandle === fallbackHandleId}
          onClick={handleClick(fallbackHandleId)}
        />
      </div>
    );
  }

  const conditionals = collectVariableNode?.slotConditionals ?? [];

  return (
    <div className='w-full'>
      {conditionals.map((conditional, idx: number) => {
        const handleId = `${nodeId}-slot-conditional-handle-${idx}`;
        return (
          <SlotButton
            key={conditional.slotValue ?? idx}
            text={conditional.slotValue}
            handleId={handleId}
            isConnectable={isConnectable}
            isHighlighted={highlightedHandle === handleId}
            onClick={handleClick(handleId)}
          />
        );
      })}
      <DefaultButton
        handleId={fallbackHandleId}
        isConnectable={isConnectable}
        isClickable={false}
        isHighlighted={highlightedHandle === fallbackHandleId}
        onClick={handleClick(fallbackHandleId)}
      />
    </div>
  );
}

export default SlotConditionals;
